import { FormGroup } from '@angular/forms';
import { ConsultationStatus } from '@enums/consultation-status';
import { ConsultationTreatmentTypes } from '@enums/consultation-treatment-types';
import { ConsultationTypes } from '@enums/consultation-types';
import { ConsultationRequestRequest } from '@models/consultation-request/consultation-request-request';
import { MedicalHistoryQuestion } from '@models/medical-history-question';

export class TreatmentConsultationRequest extends ConsultationRequestRequest {
  public consultation_status: ConsultationStatus;
  public type = ConsultationTypes.Asynchronous;

  public constructor(
    patientInfoFormGroup: FormGroup,
    medicalHistoryFormGroup: FormGroup,
    pharmacyId: number,
    treatmentType: ConsultationTreatmentTypes,
    customMedicalHistoryQuestions: MedicalHistoryQuestion[] = [],
    consultationStatus: ConsultationStatus = ConsultationStatus.Incomplete,
  ) {
    super();
    this.consultation_status = consultationStatus;
    this.setPatientInfo(patientInfoFormGroup);
    this.setMedicalHistory(medicalHistoryFormGroup, customMedicalHistoryQuestions);
    this.pharmacy_id = pharmacyId;
    this.treatment_type = treatmentType;
    this.setAdditionalInfo();
  }

  /**
   * Sets the patient information based on the provided FormGroup.
   *
   * @param {FormGroup} patientInfoFormGroup the form group containing patient information
   */
  private setPatientInfo(patientInfoFormGroup: FormGroup): void {
    const formValues = patientInfoFormGroup.value;
    const { month, day, year } = formValues.birthday;
    this.date_of_birth = new Date(year, month - 1, day).toISOString().slice(0, 10).replace('T', ' ');
    this.email = formValues.email;
    this.first_name = formValues.firstName;
    this.last_name = formValues.lastName;
    this.phone = formValues.phone;
    this.sex = formValues.sex;
  }

  /**
   * Sets the medical history fields on the consultation request.
   *
   * Uses custom questions when provided, otherwise falls back to the standard yes/no form controls.
   *
   * @param {FormGroup} medicalHistoryFormGroup the form group containing medical history values
   * @param {MedicalHistoryQuestion[]} customMedicalHistoryQuestions optional custom questions
   */
  private setMedicalHistory(
    medicalHistoryFormGroup: FormGroup,
    customMedicalHistoryQuestions: MedicalHistoryQuestion[],
  ): void {
    if (customMedicalHistoryQuestions.length > 0) {
      this.setCustomMedicalHistory(medicalHistoryFormGroup, customMedicalHistoryQuestions);

      return;
    }

    const formValues = medicalHistoryFormGroup.value;

    this.allergies = formValues.allergiesDetails;
    this.health_info = formValues.otherConditionsDetails;
    this.prescriptions = formValues.medicationDetails;
  }

  /**
   * Builds consultation medical history fields from custom questions.
   *
   * Answers are formatted as "Question label: answer", grouped by consultationRequestKey, and joined with "-".
   *
   * @param {FormGroup} medicalHistoryFormGroup the form group that holds the custom question controls
   * @param {MedicalHistoryQuestion[]} customMedicalHistoryQuestions the custom questions to process
   */
  private setCustomMedicalHistory(
    medicalHistoryFormGroup: FormGroup,
    customMedicalHistoryQuestions: MedicalHistoryQuestion[],
  ): void {
    this.allergies = '';
    this.prescriptions = '';
    this.health_info = '';

    customMedicalHistoryQuestions.forEach((question) => {
      const value = medicalHistoryFormGroup.value[question.answerOverrideQuestionId || question.id];
      const shouldSkipSubmission =
        !question.consultationRequestKey ||
        value === null ||
        value === undefined ||
        value === '' ||
        (Array.isArray(value) && value.length === 0);

      if (shouldSkipSubmission) {
        return;
      }

      const questionLabel = question.includeQuestionLabel ? `${question.label || question.placeholder}: ` : '';
      const formatted = `${questionLabel}${Array.isArray(value) ? value.join(', ') : value}`;
      const current = this[question.consultationRequestKey];

      this[question.consultationRequestKey] = current ? `${current} - ${formatted}` : formatted;
    });
  }
}
